/**
 * Minimal toast primitive, mounted at the app shell so any route can
 * `pushToast()` from an effect or an event handler.
 *
 * Intentionally small — we may swap in something richer later, but `useToast()`
 * is the API call sites depend on and it is stable.
 */

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";

export type ToastKind = "error" | "success" | "info";

export type Toast = {
  id: string;
  kind: ToastKind;
  title: string;
  /** Optional secondary line (e.g. an error code or a retry hint). */
  body?: string;
};

type ToastInput = Omit<Toast, "id"> & { id?: string };

export type ToastContextValue = {
  toasts: ReadonlyArray<Toast>;
  pushToast: (input: ToastInput) => string;
  dismissToast: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_TTL_MS = 6_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ReadonlyArray<Toast>>([]);
  const baseId = useId();

  const pushToast = useCallback(
    (input: ToastInput): string => {
      const id = input.id ?? `${baseId}-${crypto.randomUUID()}`;
      const toast: Toast =
        input.body !== undefined
          ? { id, kind: input.kind, title: input.title, body: input.body }
          : { id, kind: input.kind, title: input.title };
      setToasts((prev) => [...prev, toast]);
      return id;
    },
    [baseId],
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(
    () => ({ toasts, pushToast, dismissToast }),
    [toasts, pushToast, dismissToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} dismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be called inside <ToastProvider />");
  return ctx;
}

function ToastViewport({
  toasts,
  dismiss,
}: {
  toasts: ReadonlyArray<Toast>;
  dismiss: (id: string) => void;
}) {
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex flex-col items-end gap-2 px-6"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} dismiss={dismiss} />
      ))}
    </div>
  );
}

function ToastCard({ toast, dismiss }: { toast: Toast; dismiss: (id: string) => void }) {
  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(toast.id), DEFAULT_TTL_MS);
    return () => window.clearTimeout(timer);
  }, [toast.id, dismiss]);

  // A strip laid on top of the page: printed stock, squared, and filed under
  // the bar down its leading edge. Only failure is coloured — a success toast
  // that shouts is a toast that trains you to dismiss the one that mattered.
  const bar =
    toast.kind === "error" ? "bg-hold" : toast.kind === "success" ? "bg-cleared" : "bg-rule-strong";

  return (
    <output className="strip pointer-events-auto block max-w-sm px-4 py-3 pl-5 shadow-[var(--shadow-raise)]">
      <span aria-hidden="true" className={`strip-bar ${bar}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[0.9375rem] font-medium leading-snug text-ink">{toast.title}</div>
          {toast.body && <div className="t-field mt-1.5">{toast.body}</div>}
        </div>
        <button
          type="button"
          onClick={() => dismiss(toast.id)}
          className="-mt-0.5 -mr-1 px-1 text-ink-3 transition-colors hover:text-ink"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </output>
  );
}
